import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  AlertTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Collapse,
  Tooltip,
  TextField,
  Autocomplete
} from '@mui/material';
import { School, TipsAndUpdates, Close, InfoOutlined } from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states';
import { AiActions } from '../../Services/_actions';
import CommonTable, { EllipsisWithTooltip } from '../Shared/Tables/TableV1.tsx';

const TeacherDashboard = () => {
  const [selectedClass, setSelectedClass] = useState('09-10');
  const [selectedSubject, setSelectedSubject] = useState('Physics');
  const [teacherReport, setTeacherReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [testData, setTestData] = useState<any[]>([]);
  const [loadingTestData, setLoadingTestData] = useState(false);
  const [selectedTest, setSelectedTest] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [topicFilter, setTopicFilter] = useState<string | null>(null);
  const [subtopicFilter, setSubtopicFilter] = useState<string | null>(null);

  const actions = AiActions();
  const loggedInUser = useRecoilValue(userInformationAtom);

  const classGroups = ['06-08', '09-10', '11-12'];
  const subjects = ['Physics', 'Chemistry', 'Mathematics', 'Biology'];
  const expandedSectionRef = useRef<HTMLDivElement | null>(null);

  const getTeacherReport = async () => {
    if (!selectedClass || !selectedSubject) return;
    
    setLoadingReport(true);
    try {
      const response = await actions.getTeacherReport(selectedClass, selectedSubject);
      
      if (response?.status === 200 && response?.data) {
        setTeacherReport(response.data);
      }
    } catch (error) {
      console.error('Failed to get teacher report', error);
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (selectedClass && selectedSubject) {
      getTeacherReport();
      setTopicFilter(null);
      setSubtopicFilter(null);
    }
  }, [selectedClass, selectedSubject]);

  const handleStudentsClick = async (item) => {
    const rowKey = `${item.topic}-${item.sub_topic}`;

    if (expandedRow === rowKey) {
      setExpandedRow(null);
      setTestData([]);
      return;
    }

    setExpandedRow(rowKey);
    setLoadingTestData(true);

    try {
      const payload = {
        class_group: selectedClass,
        subject: selectedSubject,
        topic: item.topic,
        subtopic: item.sub_topic,
      //  user_id: loggedInUser?.id,
      };

      const response = await actions.getTestData(payload);

      if (response?.status === 200) {
        setTestData(response.data || []);

        setTimeout(() => {
          expandedSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching test data', error);
    } finally {
      setLoadingTestData(false);
    }
  };

  const handleTestIdClick = (test) => {
    setSelectedTest(test);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedTest(null);
  };

  const getPerformanceBandColor = (band) => {
    switch (band) {
      case 'Excellent': return { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' };
      case 'Good': return { bg: '#e3f2fd', text: '#1565c0', border: '#90caf9' };
      case 'Moderate': return { bg: '#fff4e6', text: '#ed6c02', border: '#ffcc80' };
      case 'Needs Attention': return { bg: '#ffebee', text: '#d32f2f', border: '#ef9a9a' };
      default: return { bg: '#f5f5f5', text: '#616161', border: '#e0e0e0' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return { bg: '#ffebee', text: '#c62828' };
      case 'medium': return { bg: '#fff3e0', text: '#ef6c00' };
      case 'low': return { bg: '#e8f5e9', text: '#2e7d32' };
      default: return { bg: '#f5f5f5', text: '#616161' };
    }
  };

  // Get unique topics and subtopics for filters
  const uniqueTopics = useMemo(() => {
    if (!teacherReport?.subtopics) return [];
    return [...new Set(teacherReport.subtopics.map(item => item.topic))];
  }, [teacherReport]);

  const uniqueSubtopics = useMemo(() => {
    if (!teacherReport?.subtopics) return [];
    let filtered = teacherReport.subtopics;
    if (topicFilter) {
      filtered = filtered.filter(item => item.topic === topicFilter);
    }
    return [...new Set(filtered.map(item => item.sub_topic))];
  }, [teacherReport, topicFilter]);

  // Filtered data based on topic and subtopic
  const filteredData = useMemo(() => {
    if (!teacherReport?.subtopics) return [];
    let filtered = teacherReport.subtopics;
    
    if (topicFilter) {
      filtered = filtered.filter(item => item.topic === topicFilter);
    }
    if (subtopicFilter) {
      filtered = filtered.filter(item => item.sub_topic === subtopicFilter);
    }
    
    return filtered;
  }, [teacherReport, topicFilter, subtopicFilter]);

  const topicColumns = useMemo(() => [
    {
      accessorKey: 'topic',
      header: 'Topic',
      size: 150,
      enableSorting: false,
      Cell: ({ cell }) => (
        <EllipsisWithTooltip text={cell.getValue()} maxWidth={140} fontSize={12} fontWeight={600} />
      ),
    },
    {
      accessorKey: 'sub_topic',
      header: 'Subtopic',
      size: 150,
      enableSorting: false,
      Cell: ({ cell }) => (
        <EllipsisWithTooltip text={cell.getValue()} maxWidth={140} fontSize={12} />
      ),
    },
    {
      accessorKey: 'students_attempted',
      header: 'Students',
      size: 80,
      enableSorting: false,
      Cell: ({ cell, row }) => (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: '#2563eb',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          onClick={() => handleStudentsClick(row.original)}
        >
          {cell.getValue()}
        </Typography>
      ),
    },
    // {
    //   accessorKey: 'participation_rate',
    //   header: 'Participation',
    //   size: 90,
    //   enableSorting: false,
    //   Cell: ({ cell }) => (
    //     <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
    //       {cell.getValue()}%
    //     </Typography>
    //   ),
    // },
    {
      accessorKey: 'avg_score',
      header: 'Avg Score',
      size: 80,
      enableSorting: false,
      Cell: ({ cell }) => (
        <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
          {cell.getValue()}%
        </Typography>
      ),
    },
    {
      accessorKey: 'pass_rate',
      header: 'Pass Rate',
      size: 80,
      enableSorting: false,
      Cell: ({ cell }) => (
        <Typography sx={{ fontWeight: 600, fontSize: 12 }}>
          {cell.getValue()}%
        </Typography>
      ),
    },
    {
      accessorKey: 'performance_band',
      header: 'Status',
      size: 120,
      enableSorting: false,
      Cell: ({ cell }) => {
        const bandColor = getPerformanceBandColor(cell.getValue());
        return (
          <Chip
            label={cell.getValue()}
            size="small"
            sx={{
              bgcolor: bandColor.bg,
              color: bandColor.text,
              border: `1px solid ${bandColor.border}`,
              fontSize: 9,
              height: 20,
            }}
          />
        );
      },
    },
    {
      accessorKey: 'total_tests',
      header: 'Tests',
      size: 60,
      enableSorting: false,
      Cell: ({ cell }) => (
        <Typography sx={{ fontSize: 12 }}>
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: 'avg_time_seconds',
      header: 'Avg Time',
      size: 80,
      enableSorting: false,
      Cell: ({ cell }) => (
        <Typography sx={{ fontSize: 12 }}>
          {Math.floor(cell.getValue() / 60)}m {cell.getValue() % 60}s
        </Typography>
      ),
    },
    {
      accessorKey: 'last_test_date',
      header: 'Last Test',
      size: 100,
      enableSorting: false,
      Cell: ({ cell }) => (
        <Typography sx={{ fontSize: 12 }}>
          {new Date(cell.getValue()).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      accessorKey: 'recommendation',
      header: 'Recommendation',
      size: 120,
      enableSorting: false,
      Cell: ({ cell, row }) => {
        const rec = cell.getValue();
        if (!rec) return <Typography sx={{ fontSize: 9 }}>-</Typography>;
        
        const priorityColor = getPriorityColor(rec.priority);
        return (
          <Tooltip title={rec.text} arrow placement="top">
            <Chip
              label={rec.priority.toUpperCase()}
              size="small"
              icon={<InfoOutlined sx={{ fontSize: 12 }} />}
              sx={{
                bgcolor: priorityColor.bg,
                color: priorityColor.text,
                fontSize: 8,
                height: 20,
                cursor: 'help',
                '& .MuiChip-icon': {
                  fontSize: 12,
                  color: priorityColor.text
                }
              }}
            />
          </Tooltip>
        );
      },
    },
  ], [selectedClass, selectedSubject]);

  const testDataColumns = useMemo(() => [
    {
      accessorKey: 'user_id',
      header: 'Student ID',
      size: 120,
      enableSorting: false,
      Cell: ({ cell }) => (
        <EllipsisWithTooltip text={cell.getValue()} maxWidth={110} fontSize={12} />
      ),
    },
    {
      accessorKey: 'test_id',
      header: 'Test ID',
      size: 120,
      enableSorting: false,
      Cell: ({ cell, row }) => (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: '#2563eb',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          onClick={() => handleTestIdClick(row.original)}
        >
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: 'score',
      header: 'Score',
      size: 70,
      enableSorting: false,
      Cell: ({ cell }) => (
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
          {cell.getValue() ?? '-'}
        </Typography>
      ),
    },
    {
      accessorKey: 'answered_questions',
      header: 'Answered',
      size: 90,
      enableSorting: false,
      Cell: ({ cell, row }) => (
        <Typography sx={{ fontSize: 12 }}>
          {cell.getValue()}/{row.original.total_questions}
        </Typography>
      ),
    },
    {
      accessorKey: 'test_submitted',
      header: 'Status',
      size: 80,
      enableSorting: false,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue() ? 'Submitted' : 'Pending'}
          size="small"
          sx={{
            bgcolor: cell.getValue() ? '#e8f5e9' : '#ffebee',
            color: cell.getValue() ? '#2e7d32' : '#d32f2f',
            fontSize: 9,
            height: 18,
          }}
        />
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      size: 150,
      enableSorting: false,
      Cell: ({ cell }) => (
        <EllipsisWithTooltip 
          text={new Date(cell.getValue()).toLocaleString()} 
          maxWidth={140} 
          fontSize={12} 
        />
      ),
    },
  ], []);

  return (
    <Box sx={{ p: 1.5, bgcolor: '#fafbfc', minHeight: '100vh' }}>
      {/* Header with Filters */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 1.5,
        flexWrap: 'wrap',
        gap: 1.5
      }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: '#1a1a1a',
              fontFamily: '"DM Sans", sans-serif',
              mb: 0,
              fontSize: 18,
              lineHeight: 1.2
            }}
          >
            Teacher Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
            Monitor class performance metrics
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} flexWrap="wrap"  sx={{width:{sm:'auto', xs:'100%'},gap:{sm:1, xs:'15px'}}}>
          <FormControl size="small" sx={{ minWidth: {sm:130, xs:'100%'} }}>
            <InputLabel sx={{ fontSize: 13 }}>Class</InputLabel>
            <Select
              value={selectedClass}
              label="Class"
              onChange={(e) => setSelectedClass(e.target.value)}
              sx={{ fontSize: 11, height: 32 }}
            >
              {classGroups.map((group) => (
                <MenuItem key={group} value={group} sx={{ fontSize: 11 }}>
                  {group}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: {sm:130, xs:'100%'}, ml:{sm:'auto', xs:'0px !important'} }}>
            <InputLabel sx={{ fontSize: 13 }}>Subject</InputLabel>
            <Select
              value={selectedSubject}
              label="Subject"
              onChange={(e) => setSelectedSubject(e.target.value)}
              sx={{ fontSize: 11, height: 32 }}
            >
              {subjects.map((subject) => (
                <MenuItem key={subject} value={subject} sx={{ fontSize: 11 }}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Loading State */}
      {loadingReport && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={28} sx={{ color: '#2563eb' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: 11 }}>
            Loading data...
          </Typography>
        </Box>
      )}

      {/* Report Data */}
      {!loadingReport && teacherReport && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={6} sm={2.4}>
              <Card sx={{ 
                borderRadius: 1.5, 
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)', 
                border: '1px solid #e5e7eb',
              }}>
                <CardContent sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 9, fontWeight: 600, textTransform: 'uppercase' }}>
                    Topics
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2563eb', my: 0.3, fontSize: 20, lineHeight: 1 }}>
                    {teacherReport.summary?.total_topics || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={2.4}>
              <Card sx={{ borderRadius: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 9, fontWeight: 600, textTransform: 'uppercase' }}>
                    Subtopics
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#059669', my: 0.3, fontSize: 20, lineHeight: 1 }}>
                    {teacherReport.summary?.total_subtopics || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={2.4}>
              <Card sx={{ borderRadius: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 9, fontWeight: 600, textTransform: 'uppercase' }}>
                    Avg Score
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#7c3aed', my: 0.3, fontSize: 20, lineHeight: 1 }}>
                    {teacherReport.summary?.overall_avg_score || 0}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={2.4}>
              <Card sx={{ borderRadius: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 9, fontWeight: 600, textTransform: 'uppercase' }}>
                    Participation
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#dc2626', my: 0.3, fontSize: 20, lineHeight: 1 }}>
                    {teacherReport.summary?.overall_participation_rate || 0}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={2.4}>
              <Card sx={{ borderRadius: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 9, fontWeight: 600, textTransform: 'uppercase' }}>
                    Enrolled Students
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#f59e0b', my: 0.3, fontSize: 20, lineHeight: 1 }}>
                    {teacherReport.total_students_enrolled || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Topic Performance Table */}
          <Card sx={{ 
            mb: 1.5, 
            borderRadius: 1.5, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)', 
            border: '1px solid #e5e7eb' 
          }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1a1a1a',
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 13
                  }}
                >
                  Topic Performance
                </Typography>

                <Stack direction="row" spacing={1}>
                  <Autocomplete
                    size="small"
                    options={uniqueTopics}
                    value={topicFilter}
                    onChange={(e, newValue) => {
                      setTopicFilter(newValue);
                      setSubtopicFilter(null);
                    }}
                    renderInput={(params) => <TextField {...params} label="Filter Topic" />}
                    sx={{ 
                      minWidth: 150,
                      '& .MuiInputBase-root': { fontSize: 12, height: 32 },
                      '& .MuiInputLabel-root': { fontSize: 12 }
                    }}
                  />
                  <Autocomplete
                    size="small"
                    options={uniqueSubtopics}
                    value={subtopicFilter}
                    onChange={(e, newValue) => setSubtopicFilter(newValue)}
                    disabled={!topicFilter}
                    renderInput={(params) => <TextField {...params} label="Filter Subtopic" />}
                    sx={{ 
                      minWidth: 150,
                      '& .MuiInputBase-root': { fontSize: 12, height: 32 },
                      '& .MuiInputLabel-root': { fontSize: 12 }
                    }}
                  />
                </Stack>
              </Box>

              <CommonTable
                columns={topicColumns}
                data={filteredData}
                enableSorting={false}
                enableStickyHeader={true}
                muiTableContainerProps={{ sx: { maxHeight: '350px' } }}
              />

              {/* Expanded Test Data */}
              {expandedRow && (
                <Collapse in={!!expandedRow}>
                  <Box
                    ref={expandedSectionRef}
                    sx={{ mt: 1.5, p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}
                  >
                    <Typography 
                      variant="subtitle2" 
                      sx={{ mb: 1, fontWeight: 600, fontSize: 12, color: '#374151' }}
                    >
                      Student Test Details
                    </Typography>

                    {loadingTestData ? (
                      <Box sx={{ textAlign: 'center', py: 1.5 }}>
                        <CircularProgress size={18} />
                      </Box>
                    ) : testData.length === 0 ? (
                      <Typography sx={{ fontSize: 11, color: '#6b7280', textAlign: 'center', py: 1.5 }}>
                        No test data available
                      </Typography>
                    ) : (
                      <CommonTable
                        columns={testDataColumns}
                        data={testData}
                        enableSorting={false}
                        enableStickyHeader={true}
                        muiTableContainerProps={{ sx: { maxHeight: '250px' } }}
                      />
                    )}
                  </Box>
                </Collapse>
              )}
            </CardContent>
          </Card>

          {/* Recommendations */}
          {/* {teacherReport.subtopics?.some(item => item.recommendation) && (
            <Card sx={{ 
              borderRadius: 1.5, 
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)', 
              border: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff'
            }}>
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                  <TipsAndUpdates sx={{ fontSize: 18 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"DM Sans", sans-serif', fontSize: 13 }}>
                    Recommendations
                  </Typography>
                </Box>

                <Stack spacing={0.8}>
                  {teacherReport.subtopics
                    .filter(item => item.recommendation)
                    .map((item, idx) => {
                      const priorityColor = getPriorityColor(item.recommendation.priority);
                      return (
                        <Alert
                          key={idx}
                          severity={
                            item.recommendation.priority === 'high' ? 'error' : 
                            item.recommendation.priority === 'medium' ? 'warning' : 
                            'success'
                          }
                          sx={{ 
                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                            '& .MuiAlert-icon': { color: priorityColor.text, fontSize: 16 },
                            py: 0.4,
                            '& .MuiAlert-message': { py: 0.4, width: '100%' }
                          }}
                        >
                          <AlertTitle sx={{ 
                            fontWeight: 700, 
                            color: '#1a1a1a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 0.5,
                            fontSize: 11,
                            mb: 0.3
                          }}>
                            <span>{item.topic} - {item.sub_topic}</span>
                            <Chip 
                              label={item.recommendation.priority.toUpperCase()} 
                              size="small"
                              sx={{
                                bgcolor: priorityColor.bg,
                                color: priorityColor.text,
                                fontWeight: 700,
                                fontSize: 8,
                                height: 16,
                              }}
                            />
                          </AlertTitle>
                          <Typography sx={{ fontSize: 12, color: '#374151', lineHeight: 1.4 }}>
                            {item.recommendation.text}
                          </Typography>
                        </Alert>
                      );
                    })}
                </Stack>
              </CardContent>
            </Card>
          )} */}
        </>
      )}

      {/* Empty State */}
      {!loadingReport && !teacherReport && (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <School sx={{ fontSize: 44, color: '#d1d5db', mb: 1.2 }} />
          <Typography variant="subtitle1" sx={{ color: '#6b7280', fontWeight: 600, mb: 0.4, fontSize: 13 }}>
            No Data Available
          </Typography>
          <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: 11 }}>
            Select class and subject to view analytics
          </Typography>
        </Box>
      )}

      {/* Modal for Detailed Responses */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          borderBottom: '1px solid #e5e7eb',
          p: 1.5
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
              Test Details: {selectedTest?.test_id}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: 11, mt: 0.3 }}>
              Student: {selectedTest?.user_id} | Score: {selectedTest?.score}/{selectedTest?.total_questions * 2}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseModal} size="small" sx={{ mt: -0.5 }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5, px: 1.5 }}>
          {selectedTest?.detailed_responses?.map((response, idx) => (
            <Card key={idx} sx={{ mb: 1.5, border: '1px solid #e5e7eb', boxShadow: 'none' }}>
              <CardContent sx={{ p: 1.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 11, mb: 1, color: '#1a1a1a', lineHeight: 1.4 }}>
                  Q{idx + 1}: {response.question}
                </Typography>
                
                <Stack spacing={0.8}>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6b7280', mb: 0.3 }}>
                      Student Answer:
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#374151', lineHeight: 1.4 }}>
                      {response.user_answer}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6b7280', mb: 0.3 }}>
                      Correct Answer:
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#059669', lineHeight: 1.4 }}>
                      {response.correct_answer}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={response.is_correct ? 'Correct' : 'Incorrect'}
                      size="small"
                      sx={{
                        bgcolor: response.is_correct ? '#e8f5e9' : '#ffebee',
                        color: response.is_correct ? '#2e7d32' : '#d32f2f',
                        fontSize: 9,
                        fontWeight: 600,
                        height: 18
                      }}
                    />
                  </Box>

                  {response.reasoning && (
                    <Box sx={{ bgcolor: '#f9fafb', p: 1.2, borderRadius: 1, mt: 0.5 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6b7280', mb: 0.4 }}>
                        Reasoning:
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                        {response.reasoning}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TeacherDashboard;